import { SubscriptionObject } from '@/bt-common/services/subscription-object.class';

/**
 * The MOST-RECENT subscription by createdAt — the single subscription that drives BOTH the dunning
 * warning and the hard block. Newest-by-createdAt — NOT chargebee-priority — so a newer legacy
 * active sub correctly supersedes an older chargebee debt. Self-contained (doesn't rely on caller
 * ordering), so the two consumers below can never disagree about which subscription is authoritative.
 */
const latestSubscription = (
  subscriptions: SubscriptionObject[] | undefined
): SubscriptionObject | undefined =>
  [...(subscriptions ?? [])].sort(
    (a, b) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime()
  )[0];

/**
 * Whether the workspace's UI access should be hard-blocked for unpaid invoices.
 *
 * Driven by the MOST-RECENT subscription's dunning state (`isAccountBlocked`) rather than the
 * active one. A delinquent account is cancelled by Chargebee after its dunning window and drops
 * to Free (status `cancelled` → not "active"), but it still owes — so it must stay blocked until
 * it settles. A newer healthy subscription (re-subscribe) is the latest entry and supersedes any
 * older debt.
 */
export const computeAccountBlocked = (subscriptions: SubscriptionObject[] | undefined): boolean =>
  latestSubscription(subscriptions)?.isAccountBlocked ?? false;

export interface DunningState {
  isDunning: boolean;
  dunningDays: number;
}

/**
 * The workspace's dunning (payment-failed) warning state, driven by the same MOST-RECENT
 * subscription as the block. This deliberately mirrors `computeAccountBlocked` so the warning banner
 * and the hard block are always driven by the SAME subscription.
 *
 * Why it can't read the *active* subscription: when a debt sits on a cancelled sub while a newer (or
 * paid) sub is the active one, the active sub's `isDunning` is false — so the customer got no warning
 * yet was still hard-blocked once the debt aged past the window ("blocked with no prior warning").
 * Reading the newest sub keeps warning and block consistent.
 */
export const computeDunning = (subscriptions: SubscriptionObject[] | undefined): DunningState => {
  const latest = latestSubscription(subscriptions);
  return latest?.isDunning
    ? { isDunning: true, dunningDays: latest.dunningDays }
    : { isDunning: false, dunningDays: 0 };
};
